import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { logDebug, logInfo, logWarn, logError } from '@/utils/secureLogger';

/**
 * Conversation data structure
 */
export interface Conversation {
  id: string;
  contactId: string;
  contactName: string;
  lastMessage: string;
  lastMessageTimestamp: number;
  unreadCount: number;
  isExplicitlyRead: boolean;
  lastMessageSenderId?: string;
}

/**
 * Centralized Pinia store for messaging state
 * Replaces scattered localStorage access and manual synchronization
 */
export const useMessagingStore = defineStore('messaging', () => {
  // ========== State ==========
  
  // Conversations map: conversationId -> Conversation
  const conversations = ref<Map<string, Conversation>>(new Map());
  
  // Set of conversation IDs that user has explicitly marked as read
  const explicitlyRead = ref<Set<string>>(new Set());
  
  // Unread counts per conversation: conversationId -> count
  const unreadCounts = ref<Map<string, number>>(new Map());
  
  // Currently active conversation ID
  const activeConversationId = ref<string | null>(null);
  
  // Initialization state
  const isInitialized = ref(false);
  const isInitializing = ref(false);
  
  // User type for this store instance
  const userType = ref<'student' | 'tutor' | null>(null);
  
  // User ID
  const userId = ref<string | null>(null);
  
  // Loading state
  const isLoading = ref(false);
  
  // ========== Computed ==========
  
  /**
   * Total number of conversations with unread messages
   */
  const unreadConversationCount = computed(() => {
    return Array.from(unreadCounts.value.entries())
      .filter(([id, count]) => {
        // Only count if:
        // 1. Has unread count > 0
        // 2. Not explicitly marked as read
        return count > 0 && !explicitlyRead.value.has(id);
      })
      .length;
  });
  
  /**
   * Sorted conversations by last message timestamp (most recent first)
   */
  const sortedConversations = computed(() => {
    return Array.from(conversations.value.values())
      .sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp);
  });
  
  /**
   * Conversations with unread messages
   */
  const unreadConversations = computed(() => {
    return sortedConversations.value.filter(conv => 
      !conv.isExplicitlyRead && conv.unreadCount > 0
    );
  });
  
  /**
   * Check if badge should be displayed
   */
  const hasUnreadBadge = computed(() => {
    return isInitialized.value && unreadConversationCount.value > 0;
  });
  
  // ========== Storage Keys ==========
  
  const getStorageKeys = () => {
    if (!userType.value) return null;
    return {
      conversations: `elevateED_conversations_${userType.value}`,
      explicitlyRead: `elevateED_explicitly_read_${userType.value}`,
      unreadCounts: `elevateED_unread_counts_${userType.value}`,
    };
  };
  
  // ========== Persistence ==========
  
  /**
   * Load state from localStorage
   */
  const loadPersistedState = () => {
    const keys = getStorageKeys();
    if (!keys) return;
    
    try {
      // Load explicitly read conversations
      const storedRead = localStorage.getItem(keys.explicitlyRead);
      if (storedRead) {
        explicitlyRead.value = new Set(JSON.parse(storedRead));
        logDebug('Loaded explicitly read conversations', { 
          count: explicitlyRead.value.size 
        });
      }
      
      // Load unread counts
      const storedCounts = localStorage.getItem(keys.unreadCounts);
      if (storedCounts) {
        const countsObj = JSON.parse(storedCounts);
        unreadCounts.value = new Map(Object.entries(countsObj));
        logDebug('Loaded unread counts', { 
          conversations: unreadCounts.value.size 
        });
      }
      
      // Load conversations
      const storedConversations = localStorage.getItem(keys.conversations);
      if (storedConversations) {
        const conversationsArray = JSON.parse(storedConversations);
        conversations.value = new Map(
          conversationsArray.map((conv: Conversation) => [conv.id, conv])
        );
        logDebug('Loaded conversations', { 
          count: conversations.value.size 
        });
      }
    } catch (error) {
      logWarn('Failed to load persisted messaging state', { error });
    }
  };
  
  /**
   * Save state to localStorage
   */
  const persistState = () => {
    const keys = getStorageKeys();
    if (!keys) return;
    
    try {
      // Save explicitly read
      localStorage.setItem(
        keys.explicitlyRead,
        JSON.stringify([...explicitlyRead.value])
      );
      
      // Save unread counts
      const countsObj = Object.fromEntries(unreadCounts.value);
      localStorage.setItem(keys.unreadCounts, JSON.stringify(countsObj));
      
      // Save conversations
      const conversationsArray = Array.from(conversations.value.values());
      localStorage.setItem(keys.conversations, JSON.stringify(conversationsArray));
      
      logDebug('Persisted messaging state', {
        explicitlyReadCount: explicitlyRead.value.size,
        unreadCountsSize: unreadCounts.value.size,
        conversationsCount: conversations.value.size
      });
    } catch (error) {
      logWarn('Failed to persist messaging state', { error });
    }
  };
  
  // ========== Actions ==========
  
  /**
   * Initialize the messaging store
   */
  const initialize = async (
    uid: string,
    type: 'student' | 'tutor'
  ): Promise<void> => {
    if (isInitialized.value && userId.value === uid && userType.value === type) {
      logDebug('Messaging store already initialized', { userId: uid, userType: type });
      return;
    }
    
    if (isInitializing.value) {
      logDebug('Messaging store initialization already in progress');
      return;
    }
    
    try {
      isInitializing.value = true;
      userId.value = uid;
      userType.value = type;
      
      logInfo('Initializing messaging store', { userId: uid, userType: type });
      
      // Load persisted state first
      loadPersistedState();
      
      isInitialized.value = true;
      logInfo('Messaging store initialized successfully', { 
        userId: uid,
        userType: type,
        conversationCount: conversations.value.size 
      });
    } catch (error) {
      logError('Failed to initialize messaging store', { error, userId: uid });
      throw error;
    } finally {
      isInitializing.value = false;
    }
  };
  
  /**
   * Reset the store (on logout)
   */
  const reset = () => {
    conversations.value.clear();
    explicitlyRead.value.clear();
    unreadCounts.value.clear();
    activeConversationId.value = null;
    isInitialized.value = false;
    isInitializing.value = false;
    userType.value = null;
    userId.value = null;
    isLoading.value = false;
    
    // Clear localStorage
    const keys = getStorageKeys();
    if (keys) {
      Object.values(keys).forEach(key => localStorage.removeItem(key));
    }
    
    logInfo('Messaging store reset');
  };
  
  /**
   * Update a single conversation
   */
  const updateConversation = (conversation: Conversation) => {
    conversations.value.set(conversation.id, conversation);
    
    // Update unread count
    if (conversation.unreadCount > 0) {
      unreadCounts.value.set(conversation.id, conversation.unreadCount);
    } else {
      unreadCounts.value.delete(conversation.id);
    }
    
    // Update explicitly read status
    if (conversation.isExplicitlyRead) {
      explicitlyRead.value.add(conversation.id);
    } else {
      explicitlyRead.value.delete(conversation.id);
    }
    
    persistState();
    
    logDebug('Updated conversation', { 
      conversationId: conversation.id.substring(0, 8) + '***',
      unreadCount: conversation.unreadCount,
      isExplicitlyRead: conversation.isExplicitlyRead
    });
  };
  
  /**
   * Bulk update conversations
   */
  const setConversations = (newConversations: Conversation[]) => {
    conversations.value.clear();
    unreadCounts.value.clear();
    
    newConversations.forEach(conv => {
      conversations.value.set(conv.id, conv);
      if (conv.unreadCount > 0 && !conv.isExplicitlyRead) {
        unreadCounts.value.set(conv.id, conv.unreadCount);
      }
    });
    
    persistState();
    
    logInfo('Set conversations', { 
      count: newConversations.length,
      withUnread: Array.from(unreadCounts.value.keys()).length
    });
  };
  
  /**
   * Mark a conversation as explicitly read
   */
  const markAsRead = async (conversationId: string): Promise<void> => {
    try {
      // Add to explicitly read set
      explicitlyRead.value.add(conversationId);
      
      // Clear unread count
      unreadCounts.value.delete(conversationId);
      
      // Update conversation
      const conversation = conversations.value.get(conversationId);
      if (conversation) {
        conversation.isExplicitlyRead = true;
        conversation.unreadCount = 0;
        conversations.value.set(conversationId, conversation);
      }
      
      // Persist changes
      persistState();
      
      logInfo('Marked conversation as read', { 
        conversationId: conversationId.substring(0, 8) + '***' 
      });
    } catch (error) {
      logError('Failed to mark conversation as read', { 
        error, 
        conversationId: conversationId.substring(0, 8) + '***'
      });
      throw error;
    }
  };
  
  /**
   * Handle incoming new message
   */
  const handleNewMessage = (
    conversationId: string,
    message: string,
    timestamp: number,
    senderId: string
  ) => {
    // Remove from explicitly read if new message arrives
    if (explicitlyRead.value.has(conversationId)) {
      explicitlyRead.value.delete(conversationId);
      logInfo('Removed from explicitly read due to new message', { 
        conversationId: conversationId.substring(0, 8) + '***' 
      });
    }
    
    // Update unread count
    const currentCount = unreadCounts.value.get(conversationId) || 0;
    unreadCounts.value.set(conversationId, currentCount + 1);
    
    // Update or create conversation
    const existing = conversations.value.get(conversationId);
    if (existing) {
      existing.lastMessage = message;
      existing.lastMessageTimestamp = timestamp;
      existing.unreadCount = currentCount + 1;
      existing.isExplicitlyRead = false;
      existing.lastMessageSenderId = senderId;
      conversations.value.set(conversationId, existing);
    } else {
      // Create new conversation entry
      const newConversation: Conversation = {
        id: conversationId,
        contactId: conversationId,
        contactName: 'Unknown',
        lastMessage: message,
        lastMessageTimestamp: timestamp,
        unreadCount: 1,
        isExplicitlyRead: false,
        lastMessageSenderId: senderId
      };
      conversations.value.set(conversationId, newConversation);
    }
    
    persistState();
    
    logInfo('Handled new message', { 
      conversationId: conversationId.substring(0, 8) + '***',
      unreadCount: unreadCounts.value.get(conversationId)
    });
  };
  
  /**
   * Set unread counts from CometChat
   */
  const setUnreadCounts = (countMap: Record<string, number>) => {
    unreadCounts.value.clear();
    
    Object.entries(countMap).forEach(([id, count]) => {
      // Only set if not explicitly read and has unread count
      if (count > 0 && !explicitlyRead.value.has(id)) {
        unreadCounts.value.set(id, count);
        
        // Update conversation if exists
        const conversation = conversations.value.get(id);
        if (conversation) {
          conversation.unreadCount = count;
          conversation.isExplicitlyRead = false;
          conversations.value.set(id, conversation);
        }
      }
    });
    
    persistState();
    
    logInfo('Set unread counts from CometChat', { 
      conversationsWithUnread: unreadCounts.value.size 
    });
  };
  
  /**
   * Set active conversation
   */
  const setActiveConversation = (conversationId: string | null) => {
    activeConversationId.value = conversationId;
    logDebug('Set active conversation', { 
      conversationId: conversationId?.substring(0, 8) + '***' 
    });
  };
  
  /**
   * Get conversation by ID
   */
  const getConversation = (conversationId: string): Conversation | undefined => {
    return conversations.value.get(conversationId);
  };
  
  /**
   * Check if conversation is explicitly read
   */
  const isConversationExplicitlyRead = (conversationId: string): boolean => {
    return explicitlyRead.value.has(conversationId);
  };
  
  /**
   * Get unread count for a conversation
   */
  const getUnreadCount = (conversationId: string): number => {
    return unreadCounts.value.get(conversationId) || 0;
  };
  
  return {
    // State
    conversations,
    explicitlyRead,
    unreadCounts,
    activeConversationId,
    isInitialized,
    isInitializing,
    userType,
    userId,
    isLoading,
    
    // Computed
    unreadConversationCount,
    sortedConversations,
    unreadConversations,
    hasUnreadBadge,
    
    // Actions
    initialize,
    reset,
    updateConversation,
    setConversations,
    markAsRead,
    handleNewMessage,
    setUnreadCounts,
    setActiveConversation,
    getConversation,
    isConversationExplicitlyRead,
    getUnreadCount,
    loadPersistedState,
    persistState
  };
});
